/**
 * Centralized API configuration for Taealam frontend
 * Provides dynamic backend URL based on environment
 */

/**
 * Get the base API URL
 * @returns {string} - Backend API base URL
 */
export const getApiBaseUrl = () => {
  // Check if we're in production
  if (process.env.NODE_ENV === 'production') {
    // In production on Vercel, use relative path (same domain, serverless function)
    // This allows frontend and backend to be deployed together
    return process.env.REACT_APP_API_URL || '';
  }

  // Development: use environment variable or default to localhost
  return process.env.REACT_APP_API_URL || 'http://localhost:4001';
};

// Export the base URL
export const API_BASE_URL = getApiBaseUrl();

/**
 * API endpoints organized by feature
 * All endpoints are constructed from API_BASE_URL
 */
export const API_ENDPOINTS = {
  // Booking & Request emails
  SEND_BOOKING_REQUEST: `${API_BASE_URL}/api/send-booking-request`,
  SEND_BOOKING_STATUS: `${API_BASE_URL}/api/send-booking-status`,

  // Meeting & Video
  CREATE_GOOGLE_MEET: `${API_BASE_URL}/api/create-google-meet`,
  CREATE_GOOGLE_MEET_EVENT: `${API_BASE_URL}/api/create-google-meet-event`,

  // Recording & AI
  UPLOAD_RECORDING: `${API_BASE_URL}/api/upload-recording`,
  GENERATE_LEARNING_PACKAGE: `${API_BASE_URL}/api/generate-learning-package`,

  // XP & Gamification
  XP_RATING: `${API_BASE_URL}/api/xp/rating`,
  XP_SESSION_COMPLETE: `${API_BASE_URL}/api/xp/session-complete`,
  XP_NEW_SUBJECT: `${API_BASE_URL}/api/xp/new-subject`,

  // Admin
  ADMIN_BASE: `${API_BASE_URL}/api/admin`,
  ADMIN_TEACHERS: `${API_BASE_URL}/api/admin/teachers`,
  ADMIN_STUDENTS: `${API_BASE_URL}/api/admin/students`,
  ADMIN_BOOKINGS: `${API_BASE_URL}/api/admin/bookings`,
  ADMIN_APPROVE_TEACHER: `${API_BASE_URL}/api/admin/teachers/approve`,
  ADMIN_REJECT_TEACHER: `${API_BASE_URL}/api/admin/teachers/reject`,

  // Institute
  INSTITUTE_BASE: `${API_BASE_URL}/api/institute`,
  INSTITUTE_REGISTER: `${API_BASE_URL}/api/institute/register`,
  INSTITUTE_SESSIONS: `${API_BASE_URL}/api/institute/sessions`,
  INSTITUTE_TEACHERS: `${API_BASE_URL}/api/institute/teachers`,
  INSTITUTE_STATS: `${API_BASE_URL}/api/institute/stats`,

  // Attendance
  ATTENDANCE_CHECKIN: `${API_BASE_URL}/api/attendance/checkin`,
  ATTENDANCE_CHECKOUT: `${API_BASE_URL}/api/attendance/checkout`,
  ATTENDANCE_STATUS: `${API_BASE_URL}/api/attendance/status`,

  // Teacher signup & confirmation
  SEND_TEACHER_SIGNUP: `${API_BASE_URL}/api/send-teacher-signup`,
  TEACHER_CONFIRM: `${API_BASE_URL}/api/teacher-confirm`,

  // Payment & Tap Integration
  PAYMENT_CREATE_INTENT: `${API_BASE_URL}/api/payment/create-intent`,
  PAYMENT_STATUS: `${API_BASE_URL}/api/payment/status`,
  PAYMENT_REFUND: `${API_BASE_URL}/api/payment/refund`,
  WALLET_BALANCE: `${API_BASE_URL}/api/payment/wallet/balance`,
  WALLET_TRANSACTIONS: `${API_BASE_URL}/api/payment/wallet/transactions`,
  TEACHER_PAYOUT_REQUEST: `${API_BASE_URL}/api/teacher/payout/request`,
};

/**
 * Helper function to build URL with query parameters
 * @param {string} endpoint - Base endpoint URL
 * @param {Object} params - Query parameters
 * @returns {string} - Complete URL with query string
 */
export const buildUrlWithParams = (endpoint, params = {}) => {
  const url = new URL(endpoint);
  Object.keys(params).forEach(key => {
    if (params[key] !== undefined && params[key] !== null) {
      url.searchParams.append(key, params[key]);
    }
  });
  return url.toString();
};

export default {
  API_BASE_URL,
  API_ENDPOINTS,
  getApiBaseUrl,
  buildUrlWithParams
};
